import { Amplify, API, graphqlOperation, Storage } from 'aws-amplify';
import awsconfig from '../aws-exports';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { v4 as uuidv4 } from 'uuid';
import { createGuidance, updateGuidance } from '../graphql/mutations';
import { listGuidanceTypes, getGuidance } from '../graphql/queries';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import S3Image from './S3Image';

Amplify.configure(awsconfig);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  uploadButton: {
    margin: theme.spacing(1),
  },
  submitButton: {
    margin: theme.spacing(3, 0),
  },
}));

const AddGuidanceForm = () => {
  const { guidanceId } = useParams();
  const classes = useStyles();
  const [title, setTitle] = useState('');
  const [longDescription, setLongDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [guidanceTypes, setGuidanceTypes] = useState([]);
  const [confirmation, setConfirmation] = useState(false);
  const [guidanceType, setGuidanceType] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [s3Key, setS3Key] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchGuidance = async () => {
      if (guidanceId) {
        try {
          const { data } = await API.graphql(graphqlOperation(getGuidance, { id: guidanceId }));
          const guidance = data.getGuidance;
          setTitle(guidance.title);
          setLongDescription(guidance.longDesc);
          setShortDescription(guidance.shortDesc);
          setLocation(guidance.location);
          setDate(guidance.date);
          setTime(guidance.time);
          setS3Key(guidance.S3ObjectKey);
          setGuidanceType(guidance.guidanceGuidanceTypesId);

          console.log(guidance.S3ObjectKey);
          
        } catch (error) {
          console.log(error);
        }
      }
    };
  
    fetchGuidance();
  }, [guidanceId]);
  
  useEffect(() => {
    const fetchGuidanceTypes = async () => {
      try {
        const { data } = await API.graphql(graphqlOperation(listGuidanceTypes));
        setGuidanceTypes(data.listGuidanceTypes.items);
      } catch (error) {
        console.log(error);
      }
    };

    fetchGuidanceTypes();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
  
    let key = "";
    let bucketKey = "";

    const input = {
      title: title,
      lowerCaseTitle: title.toLowerCase(),
      longDesc: longDescription,
      shortDesc: shortDescription,
      lowerCaseShortDesc: shortDescription.toLowerCase(),
      deleted: false,
      guidanceGuidanceTypesId: guidanceType,
      location: location,
      date: date ? date : undefined,
      time: time ? time : undefined,
      status: 'DRAFT'
    };
    
    if (thumbnail) {
      key = "thumbnails/" + uuidv4() + "-" + thumbnail.name;
      const response = await Storage.put(key, thumbnail);
      bucketKey = response.key;
      input.S3ObjectKey = bucketKey;
    } 
  
    try {
      if (guidanceId) {
        input.id = guidanceId;
        await API.graphql(graphqlOperation(updateGuidance, { input }));
        navigate(`/add-guidance`);
      } else {
        const submitArticle = await API.graphql(graphqlOperation(createGuidance, { input }));
        const newGuidanceId = submitArticle.data.createGuidance.id;
        navigate(`/AddGuidanceContentForm/${newGuidanceId}`);
      }
      
      setConfirmation(true);
      setTitle("");
      setLongDescription("");
      setShortDescription("");
      setThumbnail(null);
      setGuidanceType("");
      setDate("");
      setTime("");
      setLocation("");
  
    } catch (error) {
      console.error(error);
    }
  };

  const handleThumbnailChange = (event) => {
    setThumbnail(event.target.files[0]);
  };

  return (
    <Box className={classes.root} component="form" onSubmit={handleSubmit}>
      <Typography variant="h4" component="h1" gutterBottom>
        Add Guidance
      </Typography>
      <TextField
        className={classes.textField}
        required
        label="Title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <TextField
        className={classes.textField}
        required
        label="Short Description"
        multiline
        minRows={4}
        value={shortDescription}
        onChange={(event) => setShortDescription(event.target.value)}
      />
      <TextField
        className={classes.textField}
        required
        label="Long Description"
        multiline
        minRows={4}
        value={longDescription}
        onChange={(event) => setLongDescription(event.target.value)}
      />
      <TextField
        className={classes.textField}
        required
        label="Guidance Type"
        select
        value={guidanceType}
        onChange={(event) => setGuidanceType(event.target.value)}
      >
        {guidanceTypes.map((type) => (
          <MenuItem key={type.id} value={type.id}>
            {type.name}
          </MenuItem>
        ))}
      </TextField>
      {guidanceType === "122b0cf2-4d0c-4b4d-a25d-ee8af8a17f1c" && (
        <>
          <TextField
            className={classes.textField}
            label="Date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            className={classes.textField}
            label="Time"
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            className={classes.textField}
            label="Location"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
        </>
      )}
      <Button
        className={classes.uploadButton}
        variant="contained"
        component="label"
        startIcon={<CloudUploadIcon />}
      >
        Upload Thumbnail
        <input type="file" hidden onChange={handleThumbnailChange} />
      </Button>
      {thumbnail && <Typography>{thumbnail.name}</Typography>}
      {s3Key && (
        <Box mt={2}>
          <Typography variant="h6">Current Thumbnail Preview:</Typography>
          <S3Image S3Key={s3Key} S3height={200} />
        </Box>
      )}
      <Button
        className={classes.submitButton}
        variant="contained"
        color="primary"
        type="submit"
      >
        Submit
      </Button>
      {confirmation && <MuiAlert severity="success">Success!</MuiAlert>}
    </Box>
  );
};

export default AddGuidanceForm;
